import { useCallback } from 'react';

import { fireCustomCrossAppAnalytic } from '@atlassiansox/analytics-cross-product-interaction-client/fire-cross-product-analytic';

// `fireCustomCrossAppAnalytic` requires the raw `createAnalyticsEvent` from
// `@atlaskit/analytics-next`. This hook is the single isolation point for that
// dependency so the rest of `user-profile-card` can consume cross-app analytics
// through `@atlaskit/teams-app-internal-analytics` only.
import { useAnalyticsEvents as useAnalyticsNextEvents } from '@atlaskit/analytics-next';

import type { TownsquareApp } from '../types';

/**
 * Returns a function that fires the "view profile clicked" cross-app analytic
 * routed to the Teams destination app. Steps:
 * 1. Build the cross-product payload using the active townsquare app.
 * 2. Forward `createAnalyticsEvent` from `@atlaskit/analytics-next` so the
 *    shared `fireCustomCrossAppAnalytic` helper can dispatch the event.
 */
export const useFireCrossAppProfileAnalytic = (
	townsquareApp: TownsquareApp | undefined,
): (() => void) => {
	const { createAnalyticsEvent } = useAnalyticsNextEvents();

	return useCallback(() => {
		if (!townsquareApp) {
			return;
		}
		fireCustomCrossAppAnalytic({
			tags: ['townsquare', 'navigationExperiences'],
			analyticsEventFireName: 'atlas',
			destinationApp: 'teams',
			bridge: `${townsquareApp}-customLink`,
			source: `townsquare-${townsquareApp}`,
			actionSubjectId: 'viewProfileLink',
			createAnalyticsEvent,
		});
	}, [townsquareApp, createAnalyticsEvent]);
};
